/**
 * TestPrintSection — print a sample garment label to check alignment
 * (moved verbatim from the old Labels tab's `TestPrintSection`).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import { GarmentLabel } from '@/features/production-orders/components/labels/GarmentLabel';
import { usePrintLabels } from '@/features/production-orders/hooks/usePrintLabels';
import { useDefaultLabelSize } from '@/features/production-orders/hooks/useLabelTemplate';
import { SAMPLE_SCENARIOS, type SampleScenarioKey } from '../components/label-layout/label-layout-types';

const SCENARIO_OPTIONS: { value: SampleScenarioKey; label: string }[] = [
  { value: 'standard', label: 'Standard POD' },
  { value: 'redo', label: 'Redo' },
  { value: 'longName', label: 'Long Name' },
  { value: 'pAndP', label: 'P&P' },
];

export function TestPrintSection() {
  const { t } = useTranslation('settings');
  const [scenario, setScenario] = useState<SampleScenarioKey>('standard');
  const { defaultLabelSize } = useDefaultLabelSize();
  const { printRef, printLabels, isPrinting } = usePrintLabels({
    showSuccessToast: true,
  });

  const sample = SAMPLE_SCENARIOS[scenario];

  const garmentProps = {
    garment: {
      id: sample.id,
      orderNumber: sample.orderNumber,
      sku: sample.sku,
      productName: sample.productName,
      size: sample.size,
      color: sample.color,
      requiresPretreat: sample.requiresPretreat,
      fulfillmentType: sample.fulfillmentType,
      itemNumber: sample.itemNumber,
      totalItems: sample.totalItems,
      shopifyProductType: sample.shopifyProductType,
    },
    binLabel: sample.binLabel,
    isRedo: sample.isRedo,
  };

  return (
    <div className="space-y-4">
      {/* Scenario picker */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium whitespace-nowrap">
          {t('production.labels.sampleScenario')}
        </label>
        <Select
          value={scenario}
          onValueChange={(v) => setScenario(v as SampleScenarioKey)}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCENARIO_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Visible label preview */}
      <div className="flex justify-center rounded-lg border border-dashed border-border bg-muted/30 p-6">
        <div
          style={{
            transform: 'scale(0.75)',
            transformOrigin: 'top center',
          }}
        >
          <GarmentLabel {...garmentProps} />
        </div>
      </div>

      {/* Hidden print container */}
      <div ref={printRef} style={{ display: 'none' }}>
        <GarmentLabel {...garmentProps} />
      </div>

      {/* Print button */}
      <Button
        onClick={() => printLabels('', defaultLabelSize, 'Test Label')}
        disabled={isPrinting}
        className="gap-2"
      >
        <Printer className="h-4 w-4" />
        {isPrinting ? t('production.labels.printing') : t('production.labels.printTestLabel')}
      </Button>
    </div>
  );
}
